import { ApiRespBacklogMetricsByBuckets } from '@/pages/OperationsInsights/types/BacklogMetricsResponse';
import { roundToOneDecimal } from '@/pages/OperationsInsights/utils/roundNumber';
import { calculateDurationInHour } from '@/utils';

export type BacklogSummaryComponentItem = {
    value: number | string;
    changeText: string;
    hasChange?: boolean;
    isPositive?: boolean;
    isGoodChange?: boolean;
}
export type BacklogSummariesComponentData = {
    openBacklogTotalSM?: BacklogSummaryComponentItem;
    openBacklogAtRiskSM?: BacklogSummaryComponentItem;
    openBacklogBreachedSM?: BacklogSummaryComponentItem;
    openBacklogGrowthSM?: BacklogSummaryComponentItem;
    completedBacklogRateSM?: BacklogSummaryComponentItem;
};

const undefinedBucket = () => ({
    openCount: undefined, bucket: undefined, openAtRisk: undefined, openBreached: undefined
});

export const tfBLSummariesData = (response: ApiRespBacklogMetricsByBuckets): BacklogSummariesComponentData => {

    const backlogSummary = {
        openBacklogTotalSM: {
            value: 0,
            changeText: '',
            hasChange: false,
            isPositive: false,
            isGoodChange: false
        },
        openBacklogAtRiskSM: {
            value: 0,
            changeText: '',
            hasChange: false,
            isPositive: false,
            isGoodChange: false
        },
        openBacklogBreachedSM: {
            value: 0,
            changeText: '',
            hasChange: false,
            isPositive: false,
            isGoodChange: false
        },
        openBacklogGrowthSM: {
            value: '',
            changeText: '',
            hasChange: false,
            isPositive: false,
            isGoodChange: false
        },
        completedBacklogRateSM: {
            value: '',
            changeText: '',
            hasChange: false,
            isPositive: false,
            isGoodChange: false
        }
    };

    // Assuming that fetchBacklogMetricsByBuckets return sorted list by timestamp
    const fbm = response.metrics.at(0) ?? undefinedBucket(); // first bucket metrics
    const lbm = response.metrics.at(-1) ?? undefinedBucket(); // last bucket metrics

    const tmpOpenBacklog = { ...genSMObjOp(fbm.openCount, lbm.openCount, false, fbm.bucket) };
    backlogSummary.openBacklogTotalSM = { ...tmpOpenBacklog };
    backlogSummary.openBacklogAtRiskSM = { ...genSMObjOp(fbm.openAtRisk, lbm?.openAtRisk, false, fbm.bucket) };
    backlogSummary.openBacklogBreachedSM = { ...genSMObjOp(fbm.openBreached, lbm.openBreached, false, fbm.bucket) };
    backlogSummary.openBacklogGrowthSM = {
        ...backlogSummary.openBacklogTotalSM,
        value: `${tmpOpenBacklog.changePercent}%`
    };

    // Completion Rate
    const periodDurationInHour = calculateDurationInHour(response.aggregationPeriod.fromDate, response.aggregationPeriod.toDate);
    let totalClosed = 0;
    response.metrics.forEach((m) => totalClosed += m.closedCount);
    const completionRatePerHour = roundToOneDecimal(totalClosed / periodDurationInHour);
    backlogSummary.completedBacklogRateSM.value = `${completionRatePerHour}/hr`;
    backlogSummary.completedBacklogRateSM.hasChange = false;
    backlogSummary.completedBacklogRateSM.changeText = `Total Completed: ${totalClosed}`;
    return backlogSummary;
};

const locale = navigator.language;

const formatDate = (fromDate: Date | undefined): string => {
    if (!fromDate) return '';
    return Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC'
    }).format(new Date(fromDate));
};

const calculateChangeText = (valueFrom: number, valueTo: number, formattedDate: string): { changeText: string; changePercent: number } => {
    const changePercent = valueFrom ? roundToOneDecimal(((valueTo - valueFrom) / valueFrom) * 100) : 0;
    const incDecText = valueTo > valueFrom ? 'increase' : 'decreased';
    const changeText = valueFrom ? `${changePercent}% ${incDecText} since ${formattedDate}` : `No data on ${formattedDate}`;
    return { changeText, changePercent };
};

/**
 * Generate summary obj for open metric
 * @param valueFrom
 * @param valueTo
 * @param isPositiveGood
 * @param fromDate
 */
const genSMObjOp = (valueFrom: number | undefined, valueTo: number | undefined, isPositiveGood: boolean, fromDate: Date | undefined) => {
    if (valueTo === undefined || valueFrom === undefined) {
        return {
            value: valueTo ?? 0,
            hasChange: false,
            isPositive: false,
            changeText: '',
            isGoodChange: false,
            changePercent: 0
        };
    }

    const hasChange = valueFrom !== valueTo;
    const formattedDate = formatDate(fromDate);

    if (!hasChange) {
        return {
            value: valueTo,
            hasChange: false,
            isPositive: false,
            changeText: `no change since ${formattedDate}`,
            isGoodChange: false,
            changePercent: 0
        };
    }

    const isPositive = valueTo > valueFrom;
    const isGoodChange = isPositiveGood ? isPositive : !isPositive;
    const { changeText, changePercent } = calculateChangeText(valueFrom, valueTo, formattedDate);

    return {
        value: valueTo,
        hasChange,
        isPositive,
        changeText,
        isGoodChange,
        changePercent
    };
};
